import React from "react";
import { Images } from "../../../../../utils/imageConstants";
import type { IMatchSummary } from "./types/IMatches";
import { useNavigate } from "react-router-dom";
import { formatDateAndTime, getMatchEquationText, getPlayerImageUrl, getTeamScores, isTestMatch } from "../../../../../helper/helper";

function liveCardStatusHeadline(match: IMatchSummary): string {
  // Complete equation depending on match status (matchUpdateString > ended
  // equation > live inning equation > toss result), with a LIVE fallback.
  const equation = getMatchEquationText(match);
  if (equation && equation !== "-") return equation.toUpperCase();
  return "LIVE";
}

function getLiveTagClass(headline: string): string {
  const upper = headline.toUpperCase();
  if (upper === "LIVE") return "live-tag";
  if (
    upper.includes("DELAY") ||
    upper.includes("RAIN") ||
    upper.includes("SUSPEND") ||
    upper.includes("INTERRUPT") ||
    upper.includes("ABANDON") ||
    upper.includes("CANCEL")
  ) {
    return "upcoming-tag";
  }
  return "live-tag";
}

type LiveMatchProps = {
  matches: IMatchSummary[];
  isLoading: boolean;
  viewList: boolean;
};

const LiveMatches = ({ matches, isLoading, viewList }: LiveMatchProps) => {
  const navigate = useNavigate();
  if (isLoading) {
    return <p>Loading…</p>;
  }

  const data: IMatchSummary[] = matches && matches.length > 0 ? matches : [];
  return (
    <div>
      {viewList ? (
        <div className="cards-container">
          {data.map((match: IMatchSummary) => (
            <div className="cards" key={match?.matchId}>
              <article className="match-card">
                <div className="meta" onClick={() => navigate(`/matches-details/${match?.matchId}`)}>
                  <span className="tag">{match?.matchType || "-"}</span>
                  <div className="details">
                    <p>
                      <strong>
                        {formatDateAndTime(match?.tournament?.startDate)}
                      </strong>{" "}
                      | {match?.leagueName ?? "-"}
                    </p>
                    <p className="sub">Umpire :</p>
                    <p className="sub">{match?.city}</p>
                  </div>
                </div>
                <div className="teams">
                  {(() => {
                    const { firstTeamScore, secondTeamScore, firstTeamScoreText, secondTeamScoreText, firstTeamName, secondTeamName, showYetToBat } = getTeamScores(match);
                    const firstTeamImage = match?.teamOne?.teamName === firstTeamName
                      ? match?.teamOne?.imageUrl
                      : match?.teamTwo?.imageUrl;
                    const secondTeamImage = match?.teamOne?.teamName === secondTeamName
                      ? match?.teamOne?.imageUrl
                      : match?.teamTwo?.imageUrl;

                    return (
                      <>
                        <div className="row">
                          <img className="logo" src={getPlayerImageUrl(firstTeamImage) || Images.card_info_image} alt="" />
                          <div className="name-and-chip">
                            <span className="team">{firstTeamName || "-"}</span>
                            <span className="chip-blue">Home</span>
                          </div>

                          {firstTeamScore ? (
                            <div className="score">
                              <span className="runs">
                                {firstTeamScoreText}
                              </span>
                              {!isTestMatch(match?.matchType) && (
                                <span className="ovr">
                                  ({firstTeamScore.over}.{firstTeamScore.ball})
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="status">YET TO BAT</div>
                          )}
                        </div>

                        <div className="row second-row">
                          <img className="logo" src={getPlayerImageUrl(secondTeamImage) || Images.card_info_image} alt="" />
                          <div className="name-and-chip">
                            <span className="team">{secondTeamName || "-"}</span>
                            <span className="chip-blue">Away</span>
                          </div>
                          {showYetToBat ? (
                            <div className="status">YET TO BAT</div>
                          ) : secondTeamScore ? (
                            <div className="score">
                              <span className="runs">
                                {secondTeamScoreText}
                              </span>
                              {!isTestMatch(match?.matchType) && (
                                <span className="ovr">
                                  ({secondTeamScore.over}.{secondTeamScore.ball})
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="status">-</div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="divider" aria-hidden="true"></div>
                <div className="cta">
                  <button
                    className="btn btn-secondary"
                    onClick={() =>
                      navigate("/matches-details", {
                        state: { matchTab: "Scorecard" },
                      })
                    }
                  >
                    Match Center &nbsp; &gt;
                  </button>
                </div>
              </article>
            </div>
          ))}
        </div>
      ) : (
        <div className="live-matches-grid">
          {data.map((match: IMatchSummary) => (
            <div className="live-match-card" key={match?.matchId}>
              <div className="live-match-content">
                <div className="live-match-header">
                  <div className="live-indicator">
                    <span className={getLiveTagClass(liveCardStatusHeadline(match))}>{liveCardStatusHeadline(match)}</span>
                    <div className="match-info">
                      <span className="match-context-league">
                        {match?.leagueName?.trim() || "—"}
                      </span>
                      <span className="separator">|</span>
                      <span className="match-context-tournament">
                        {match?.tournament?.tournamentName?.trim() || "—"}
                      </span>
                    </div>
                  </div>
                  <div className="divider-line"></div>

                  <div className="teams-section">
                    {(() => {
                      const { firstTeamScore, secondTeamScore, firstTeamScoreText, secondTeamScoreText, firstTeamName, secondTeamName, showYetToBat } = getTeamScores(match);
                      const firstTeamImage = match?.teamOne?.teamName === firstTeamName
                        ? match?.teamOne?.imageUrl
                        : match?.teamTwo?.imageUrl;
                      const secondTeamImage = match?.teamOne?.teamName === secondTeamName
                        ? match?.teamOne?.imageUrl
                        : match?.teamTwo?.imageUrl;

                      return (
                        <>
                          <div className="team-row">
                            <div className="team-info">
                              <div className="team-logo">
                                <img src={getPlayerImageUrl(firstTeamImage) || Images.card_info_image} alt="Team Logo" />
                              </div>
                              <span className="team-name">{firstTeamName || "-"}</span>
                            </div>
                            <div className="team-score">
                              {firstTeamScore ? (
                                <div className="score-with-overs">
                                  <span className="score">
                                    {firstTeamScoreText}
                                  </span>
                                  {!isTestMatch(match?.matchType) && (
                                    <span className="overs">
                                      ({firstTeamScore.over}.{firstTeamScore.ball} ov)
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="score">-</span>
                              )}
                            </div>
                          </div>

                          <div className="team-row">
                            <div className="team-info">
                              <div className="team-logo">
                                <img src={getPlayerImageUrl(secondTeamImage) || Images.card_info_image} alt="Team Logo" />
                              </div>
                              <span className="team-name">{secondTeamName || "-"}</span>
                            </div>
                            <div className="team-score">
                              {showYetToBat ? (
                                <span className="score">Yet to Bat</span>
                              ) : secondTeamScore ? (
                                <div className="score-with-overs">
                                  <span className="score">
                                    {secondTeamScoreText}
                                  </span>
                                  {!isTestMatch(match?.matchType) && (
                                    <span className="overs">
                                      ({secondTeamScore.over}.{secondTeamScore.ball} ov)
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="score">-</span>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="match-details">
                  <p className="umpire-info">
                    <span className="label">Umpire : </span>
                    <span className="value">{match?.umpire || "TBD"}</span>
                  </p>
                  <div className="venue-info">
                    <div className="venue-icon">
                      <img src={Images.blue_location} alt="Venue" />
                    </div>
                    <span className="venue-name">{match?.groundId?.groundName || match?.city}</span>
                  </div>
                </div>

                <div className="divider-line"></div>

                <button
                  className="match-center-btn"
                  onClick={() => navigate(`/matches-details/${match?.matchId}`)}
                >
                  <span>Match Center</span>
                  <div className="btn-icon">
                    <img src={Images.scorecard_icon} alt="Arrow" />
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {matches?.length === 0 &&
        <div className="no-data-found">
          <img src={Images.my_leagues_no} className="n" alt="no-data-image" />
          <p>NO MATCHES FOUND</p>
        </div>
      }
    </div>
  );
};

export default LiveMatches;